import { type ReactNode } from 'react'

import { cn } from '../cn'

// EmptyStateCard + PreviewCard (design/components/empty-state.html). A good
// empty state turns "nothing here" into a clear first step (day 1, no-results,
// permission fallback — without revealing what the user can't have). PreviewCard
// shows the SHAPE of the result in ghost form when data is insufficient — never
// invented numbers. Icon-and-words only (no emojis/illustrations in Phase 1).

const emptyTone = {
  blue: 'bg-[color-mix(in_srgb,var(--brand-blue)_8%,transparent)] text-link',
  ink: 'bg-[color-mix(in_srgb,var(--accent-ink)_8%,transparent)] text-accent-ink',
  ok: 'bg-ok-bg text-ok',
  gray: 'bg-field text-text-3',
}

const richText = '[&_b]:font-semibold [&_b]:text-text'

export interface EmptyStateCardProps {
  icon: ReactNode
  tone?: 'blue' | 'ink' | 'ok' | 'gray'
  title: string
  body: ReactNode
  actions?: ReactNode
  meta?: ReactNode
  /** Semantic intent (documentation/analytics; doesn't change layout). */
  kind?: 'day1' | 'no-results' | 'permission'
  headingLevel?: 2 | 3 | 4
  className?: string
}

export function EmptyStateCard({
  icon,
  tone = 'gray',
  title,
  body,
  actions,
  meta,
  headingLevel = 3,
  className,
}: EmptyStateCardProps) {
  const Heading = `h${headingLevel}` as const
  return (
    <div
      className={cn(
        'mx-auto max-w-[440px] rounded-xl border border-line bg-surface px-[30px] py-8 text-center',
        className,
      )}
    >
      <span
        aria-hidden
        className={cn(
          'inline-flex size-[60px] items-center justify-center rounded-xl [&_svg]:size-[26px]',
          emptyTone[tone],
        )}
      >
        {icon}
      </span>
      <Heading className="mt-[18px] text-[17px] font-bold tracking-[-0.015em] text-text">
        {title}
      </Heading>
      <p
        className={cn(
          'mx-auto mt-[9px] max-w-[42ch] text-[13px] leading-relaxed text-text-2',
          richText,
        )}
      >
        {body}
      </p>
      {actions && <div className="mt-5 flex flex-wrap justify-center gap-[9px]">{actions}</div>}
      {meta && (
        <div
          className={cn(
            'mt-[22px] border-t border-line pt-[18px] text-[11.5px] leading-normal text-text-3 [&_b]:font-semibold [&_b]:text-text-2',
          )}
        >
          {meta}
        </div>
      )}
    </div>
  )
}

function GhostLine({ w }: { w: string }) {
  return <span className="block h-[9px] rounded bg-field opacity-70" style={{ width: w }} />
}

export interface PreviewCardProps {
  title: string
  shape: 'chart' | 'stats' | 'list'
  /** Why there's no data yet (accessible — explains the ghost). */
  hint?: string
  className?: string
}

export function PreviewCard({ title, shape, hint, className }: PreviewCardProps) {
  return (
    <div className={cn('overflow-hidden rounded-[14px] border border-line bg-surface', className)}>
      <div className="flex items-center gap-2 border-b border-line px-4 py-[13px]">
        <span className="text-[12.5px] font-bold text-text">{title}</span>
        <span className="ml-auto rounded-sm bg-field px-2 py-0.5 text-[9.5px] font-bold uppercase tracking-[0.05em] text-text-3">
          Vista previa
        </span>
      </div>
      <div className="flex flex-col gap-[9px] p-4" aria-hidden>
        {shape === 'chart' && (
          <>
            <GhostLine w="80%" />
            <GhostLine w="60%" />
            <span className="mt-[5px] block h-20 rounded-lg bg-field opacity-60" />
          </>
        )}
        {shape === 'list' && (
          <>
            <GhostLine w="90%" />
            <GhostLine w="75%" />
            <GhostLine w="85%" />
            <GhostLine w="65%" />
          </>
        )}
        {shape === 'stats' && (
          <>
            <GhostLine w="80%" />
            <div className="mt-1 grid grid-cols-2 gap-[9px]">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="rounded-md border border-line bg-surface-2 p-[11px]">
                  <span className="block h-2 w-1/2 rounded bg-field opacity-70" />
                  <span className="mt-2 block h-4 w-[70%] rounded bg-field opacity-70" />
                </div>
              ))}
            </div>
          </>
        )}
      </div>
      <p className={cn('px-4 pb-3 text-center text-[11px] text-text-3', !hint && 'sr-only')}>
        {hint ?? `Vista previa de ${title}: aún no hay datos suficientes.`}
      </p>
    </div>
  )
}
